/**
 * 시간 간격/interval 관련 유틸 함수 모듈
 * @module core/time/interval
 */
import dayjs from 'dayjs';
import { ONE_WEEK, ONE_DAY, ONE_HOUR, TEN_MINUTES } from '@/common/utils/define';
import type { RequestInterval } from '@/common/utils/define';
import type { MetricV7RequestInterpolateType } from '@/api-client/data/model';

/**
 * timeRange(ms)에 따라 데이터 집계 interval 반환
 */
export const getDataArrangeIntervalByTimeRange = (timeRange: number): RequestInterval => {
  if (timeRange > ONE_WEEK) return 'I1d';
  if (timeRange > ONE_DAY) return 'I1h';
  if (timeRange > ONE_HOUR) return 'I10m';
  if (timeRange > TEN_MINUTES) return 'I1m';
  return 'I5s';
};

/**
 * 'p10m' 등 문자열을 ms로 변환
 */
export const getTimeBy = (period: string): number => {
  const pp = period.match(/[pI](\d+)([smhd])/);
  if (!pp) return 600_000;
  const [, num, unit] = pp;
  switch (unit) {
    case 's':
      return Number(num) * 1_000;
    case 'm':
      return Number(num) * 60_000;
    case 'h':
      return Number(num) * 3_600_000;
    case 'd':
      return Number(num) * 86_400_000;
    default:
      return 600_000;
  }
};

/**
 * timePeriod 문자열로부터 적절한 interval 반환
 */
export const getTimeIntervalByTimePeriod = (timePeriod: string): RequestInterval => {
  const timeRange = getTimeBy(timePeriod);
  return getDataArrangeIntervalByTimeRange(timeRange);
};

/**
 * 시작/종료 시간으로부터 interval 반환
 */
export const getDataArrangeIntervalByStartEndTime = (
  startTimeStr: string,
  endTimeStr?: string,
): RequestInterval => {
  try {
    const endTime = endTimeStr ? dayjs(endTimeStr) : dayjs.tz();
    const range = endTime.diff(dayjs(startTimeStr));
    return getDataArrangeIntervalByTimeRange(range);
  } catch {
    return 'I1m';
  }
};

/**
 * interval에 따른 보간법 반환
 */
export const getInterpolateTypeByInterval = (interval: string): MetricV7RequestInterpolateType => {
  let timeUnit: string;
  if (interval.endsWith('ms')) {
    timeUnit = 'ms';
  } else {
    timeUnit = interval[interval.length - 1];
  }
  switch (timeUnit) {
    case 'ms':
    case 's':
      return 'Linear';
    case 'm':
    case 'h':
    case 'd':
    default:
      return 'Null';
  }
};
